import angular, {IScope} from 'angular';
import BigNumber from 'bignumber.js';
import {LoanPreTerminateService} from 'components/customer/loans/pre-terminate/loan-pre-terminate.types';
import {Loan, LoanDetails, WaiveRequest} from 'components/service/loan.types';
import {ProductDefinitionService} from 'components/service/product-definition.service';
import {ProductDefinition, ProductStatus} from 'components/service/product.types';
import {ModalApi} from 'components/technical/modal/modal.component';
import {Customer} from 'customer/CustomerTypes';
import {NgTableParams} from 'ng-table';
import nxModule from 'nxModule';
import Authentication from 'shared/utils/authentication';
import {HttpService} from 'shared/utils/httpService';

import './loan-consolidation.styles.less';
import Notification from 'shared/utils/notification';
import templateUrl from './loan-consolidation.template.html';

interface ConsolidatedLoan {
  customerName: string;
  loanId: number;
  productNumber: string;
  productName?: string;
  principalAmount: number;
  principalBalance: number;
  interestBalance: number;
  cbuBalance: number;
  pfBalance: number;
  tpBalance: number;
  pastDueInterest: number;
  penalty: number;
  customFees: number;
  status: ProductStatus;
  waiveRequest?: WaiveRequest;
  showWaiveForm: boolean;
  amortizationIds?: number[];
  maxValues?: Record<string, number>;
  sourceLoan?: LoanDetails;
}

const validStatusesForConsolidation : ProductStatus[] = ['ACTIVE', 'PAST_DUE_PERFORMING', 'PAST_DUE_NON_PERFORMING', 'PAST_DUE_LITIGATION'];

export class ConsolidatedLoansComponent {
  form!: angular.IFormController;
  private loan!: Loan;
  protected waiveForms:  angular.IFormController[] = [];
  private hasAccessToAllBranches!: boolean;
  protected customerBranchIdRestriction!: number[];
  private customerSearchModal!: ModalApi;
  protected consolidatedLoansTableConfig!: NgTableParams<ConsolidatedLoan>;
  protected customerLoansTableConfig!: NgTableParams<LoanDetails>;
  protected consolidatedLoans: ConsolidatedLoan[] = [];
  protected selectedCustomer?: Customer;
  private productDefinitions!: ProductDefinition[];
  private readonly loanIdsBeingAdded = new Set<number>();

  protected principal: BigNumber = new BigNumber(0);
  protected interest: BigNumber = new BigNumber(0);
  protected cbu: BigNumber = new BigNumber(0);
  protected pf: BigNumber = new BigNumber(0);
  protected tp: BigNumber = new BigNumber(0);
  protected pdi: BigNumber = new BigNumber(0);
  protected penalty: BigNumber = new BigNumber(0);
  protected customFee: BigNumber = new BigNumber(0);

  constructor(private http: HttpService, protected notification: Notification, protected authentication: Authentication, private productDefinitionService: ProductDefinitionService, private $scope: IScope, private loanPreTerminateService: LoanPreTerminateService) {
  }

  async $onInit(): Promise<void> {
    this.consolidatedLoansTableConfig = new NgTableParams<ConsolidatedLoan>({
      page: 1,
      count: 20,
    });
    this.customerLoansTableConfig = new NgTableParams<LoanDetails>({
      page: 1,
      count: 5,
    }, {
      counts: [],
      paginationMaxBlocks: 8,
      paginationMinBlocks: 3
    });
    this.hasAccessToAllBranches = this.authentication.permissions['CST_ALL_BRANCHES_ACCESS'];
    this.customerBranchIdRestriction = this.hasAccessToAllBranches ? [] : this.authentication.context.branchIds;
    this.productDefinitions = await this.productDefinitionService.toPromise();
    this.$scope.$watch('$ctrl.consolidatedLoans', () => {
      this.loan.waiveRequests = this.consolidatedLoans
        .map(loan => {
          if (!loan.waiveRequest) return undefined;
          loan.waiveRequest.productId = loan.loanId;
          return loan.waiveRequest;
        })
        .filter((waive): waive is WaiveRequest => waive !== undefined);
      this.recalculateTotals();
    }, true)
    await this.loadExistingConsolidatedLoan();
  }

  onModalReady = ({ api }: {api: ModalApi}) : void => {
    this.customerSearchModal = api;
  };

  async showCustomerSearch() : Promise<void> {
    await this.customerSearchModal.show();
    this.customerLoansTableConfig.settings({
      dataset: []
    });
    this.selectedCustomer = undefined;
  }

  async selectCustomer(customer: Customer): Promise<void> {
    this.selectedCustomer = customer;
    const loans : LoanDetails[] = await this.http.get<LoanDetails[]>(`/products/loans?customerId=${customer.id}`).toPromise();

    const availableLoans = loans.filter(l => {
        const productDefinition = this.productDefinitions.find(pd => pd.id === l.definitionId);

        return validStatusesForConsolidation.includes(l.status) &&
          (this.hasAccessToAllBranches || this.hasBranchAccess(this.authentication.context.branchIds, l.branchId)) &&
          (!productDefinition?.employeeProduct || this.authentication.permissions['CST_EMPLOYEE_LOANS_READ']);
      })
      .map(l => {
        const productDefinition = this.productDefinitions.find(pd => pd.id === l.definitionId);
        if(productDefinition) {
          l.productDefinition = productDefinition;
        }
        return l;
      })

    this.customerLoansTableConfig.settings({
      dataset: availableLoans
    });
  }

  isLoanAlreadyAdded(loan : Loan) : boolean {
    return this.loanIdsBeingAdded.has(loan.id) || (this.loan.remadeFromLoanIds?.includes(loan.id) ?? false);
  }

  onWaiveChange(item: ConsolidatedLoan): void {
    this.loan.waiveRequests = this.consolidatedLoans
      .map(loan => {
        if (!loan.waiveRequest) return undefined;
        loan.waiveRequest.productId = loan.loanId;
        return loan.waiveRequest;
      })
      .filter((waive): waive is WaiveRequest => waive !== undefined);
    this.recalculateTotals();
  }

  async addLoan(loan: LoanDetails, fetchWaiveRequest: boolean = false): Promise<void> {
    if(!this.selectedCustomer || this.loanIdsBeingAdded.has(loan.id)) {
      return;
    }

    const loanIndex = this.consolidatedLoans.findIndex(l => l.loanId === loan.id);
    if(loanIndex >= 0) {
      this.notification.show("Error", `Loan ${loan.productNumber} has already been added.`);
      return;
    }

    // Marked before the first await so the digest that follows the click already disables
    // the Add button; the balances below take a round trip to load.
    this.loanIdsBeingAdded.add(loan.id);
    try {
      await this.appendLoan(loan, this.selectedCustomer, fetchWaiveRequest);
    } finally {
      this.loanIdsBeingAdded.delete(loan.id);
      // Native await resumes outside AngularJS' digest, so schedule one to render the outcome.
      this.$scope.$evalAsync();
    }
  }

  private async appendLoan(loan: LoanDetails, customer: Customer, fetchWaiveRequest: boolean): Promise<void> {
    let preterminationFees = 0;
    try {
      const preterminationFeeArr: any[] = await this.loanPreTerminateService.getCustomPreterminationFees(loan.id);
      preterminationFees = preterminationFeeArr.reduce((sum, fee) => sum + (fee.amount ?? 0), 0);
    } catch (e) {
      this.notification.show("Error", `Error fetching pretermination fees for loan ${loan.productNumber}.`);
      return;
    }

    let waiveRequest: WaiveRequest = {
      productId: loan.id,
      waiveParts: [],
      priorityAmortizationIds: [],
      total: {
        totalWaive: 0,
        totalOutstandingBalance: 0,
        totalFinalBalanceToPay: 0
      }
    };

    if (fetchWaiveRequest) {
      try{
        const response: WaiveRequest = await this.http.get<WaiveRequest>(`/products/loans/${loan.id}/waive-request`).toPromise();
        waiveRequest = response ? response : waiveRequest;
      } catch(e){
        this.notification.show("Error", `Error fetching waive request for loan ${loan.productNumber}.`);
      }
    }

    const balance = loan.amortizationSchedule.totalBalance;
    this.consolidatedLoans.push({
      customerName: customer.effectiveName,
      loanId: loan.id,
      productNumber: loan.productNumber,
      productName: loan.productDefinition?.productName,
      principalAmount: loan.principalAmount,
      principalBalance: loan.principalBalance,
      status: loan.status,
      interestBalance: balance?.interest ?? 0,
      cbuBalance: balance?.cbuCharge ?? 0,
      pfBalance: balance?.pfCharge ?? 0,
      tpBalance: balance?.tpCharge ?? 0,
      pastDueInterest: balance?.totalPastDueInterest ?? 0,
      penalty: balance?.totalPenalty ?? 0,
      customFees: (balance?.customFees ?? 0) + preterminationFees,
      showWaiveForm: false,
      waiveRequest: waiveRequest,
      maxValues: { ...balance },
      amortizationIds: loan.amortizationSchedule?.list.map(a => a.id) ?? [],
      sourceLoan: loan
    });

    this.recalculateTotals();

    if(!this.loan.remadeFromLoanIds) {
      this.loan.remadeFromLoanIds = [];
    }

    if(!this.loan.remadeFromLoanIds.includes(loan.id)){
      this.loan.remadeFromLoanIds.push(loan.id);
    }

    this.loan.waiveRequests = this.consolidatedLoans
      .map(loan => {
        if (!loan.waiveRequest) return undefined;
        loan.waiveRequest.productId = loan.loanId;
        return loan.waiveRequest;
      })
      .filter((waive): waive is WaiveRequest => waive !== undefined);

  }

  removeRow(index: number, loanId: number): void {
    this.consolidatedLoans.splice(index, 1);
    this.loan.remadeFromLoanIds = this.loan.remadeFromLoanIds?.filter(id => id !== loanId);
    this.recalculateTotals();
  }

  hasConsolidatedLoan() : boolean {
    return (this.loan?.remadeFromLoanIds?.length ?? 0) > 0;
  }

  hasBranchAccess(userBranchIds: number[], loanBranchId: number): boolean {
    return userBranchIds.includes(loanBranchId);
  }

  private getWaivedAmount(loan: ConsolidatedLoan, type: string): BigNumber {
    if (!loan.waiveRequest || !Array.isArray(loan.waiveRequest.waiveParts)) {
      return new BigNumber(0);
    }
    const amount = loan.waiveRequest.waiveParts
      .filter(p => p.waiveType === type)
      .map(p => {
        const value = new BigNumber(p.waiveAmount);
        return value.isFinite() ? value : new BigNumber(0);
      })
      .reduce((sum, amount) => sum.plus(amount), new BigNumber(0))
    return amount;
  }

  recalculateTotals(): void {
    this.principal = new BigNumber(0);
    this.interest = new BigNumber(0);
    this.cbu = new BigNumber(0);
    this.pf = new BigNumber(0);
    this.tp = new BigNumber(0);
    this.pdi = new BigNumber(0);
    this.penalty = new BigNumber(0);
    this.customFee = new BigNumber(0);

    for(const loan of this.consolidatedLoans) {
      this.principal = this.principal.plus(new BigNumber(loan.principalBalance));
      this.interest = this.interest.plus(new BigNumber(loan.interestBalance).minus(new BigNumber(this.getWaivedAmount(loan, 'INTEREST'))));
      this.cbu = this.cbu.plus(new BigNumber(loan.cbuBalance).minus(new BigNumber(this.getWaivedAmount(loan, 'CBU'))));
      this.pf = this.pf.plus(new BigNumber(loan.pfBalance).minus(new BigNumber(this.getWaivedAmount(loan, 'PF'))));
      this.tp = this.tp.plus(new BigNumber(loan.tpBalance).minus(new BigNumber(this.getWaivedAmount(loan, 'TP'))));
      this.pdi = this.pdi.plus(new BigNumber(loan.pastDueInterest).minus(new BigNumber(this.getWaivedAmount(loan, 'PAST_DUE_INTEREST'))));
      this.penalty = this.penalty.plus(new BigNumber(loan.penalty).minus(new BigNumber(this.getWaivedAmount(loan, 'PENALTY'))));
      this.customFee = this.customFee.plus(new BigNumber(loan.customFees).minus(new BigNumber(this.getWaivedAmount(loan, 'CUSTOM_FEE'))));
    }
  }

  async loadExistingConsolidatedLoan() : Promise<void>{
    if(this.isInactiveLoanDueToLoanConsolidation() && this.loan.remadeFromLoanIds){
      for (const loanId of this.loan.remadeFromLoanIds) {
        const loanDetails = await this.http.get<LoanDetails>(`/products/loans/${loanId}`).toPromise();
        const customer = await this.http.get<Customer>(`/customers/${loanDetails.customerId}`).toPromise();
        await this.selectCustomer(customer);

        await this.addLoan(loanDetails, true);
      }
    }
  }

  isInactiveLoanDueToLoanConsolidation (): boolean {
    return this.loan.creationType === 'CONSOLIDATION' && this.loan.status === 'INACTIVE' && (this.loan.remadeFromLoanIds?.length ?? 0) > 0;
  }
}

nxModule.component('loanConsolidation', {
  templateUrl,
  bindings: {
    loan: '='
  },
  require: {
    form: '?^form',
  },
  controller: ConsolidatedLoansComponent
});
